import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { Box, Button, Flex, HStack, useToast, useDisclosure } from "@chakra-ui/react";
import { Formik } from "formik";

import Typography from "@/components/Common/Typography";
import V2Service from "@/services/v2Service";
import { goToNextStep, setActiveStep, setValues, resetState, setSendAmount, setReceivedAmount, getBalanceAccounts } from "@/store/slices/sendMoneySlices";

import IndividualPaymentLoader from "./IndividualPaymentLoader";
import SelectContactTab from "./Tabs/SelectContactTab";
import SetAmountTab from "./Tabs/SetAmountTab";
import { getValidationSchema } from "./individualPaymentSchema";

// CSS
import "../sendmoney.css";

const IndividualPayment = () => {
  const toast = useToast();
  const formikRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // eslint-disable-next-line no-unused-vars
  const { onOpen, isOpen, onClose } = useDisclosure();

  const { step: activeStep, firstStepComplete, loading, values: formValues } = useSelector((state) => state.sendMoney);
  const balanceDetails = useSelector((state) => state.sendMoney.values.walletName);

  const validationSchema = getValidationSchema();
  const currentValidationSchema = validationSchema[activeStep - 1];

  // eslint-disable-next-line no-unused-vars
  const [tab, setTab] = useState("select-contact");
  const [walletList, setWalletList] = useState([]);

  const tabMap = {
    1: "select-contact",
    2: "set-amount",
    3: "summary",
  };

  const initialValue = {
    enteredAmount: 0, // depositAmount
    receivedAmount: 0, // withdrawAmount
    walletName: "",
    payoutCurrency: "",
    paymentCode: "",
    contactId: "",
  };

  const handleToast = (title, type) => {
    toast({
      title,
      status: type,
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  };

  function handleTabChange({ tabNumber }) {
    setTab(tabMap[tabNumber]);
    dispatch(setActiveStep(tabNumber));

    // Reset States Upon Moving back Down States
    if (tabNumber === 1) {
      formikRef?.current?.setFieldValue("enteredAmount", 0);
      formikRef?.current?.setFieldValue("receivedAmount", 0);
      dispatch(setSendAmount(0));
      dispatch(setReceivedAmount(0));
    }
  }

  async function handleSubmit(values, actions) {
    actions.setSubmitting(true);
    if (activeStep === 2) {
      const roundedDegree = balanceDetails?.label === "BTC" ? 5 : 2;

      if (Number(Number(balanceDetails?.balance || 0).toFixed(roundedDegree)) < Number(Number(values.enteredAmount || 0).toFixed(roundedDegree))) {
        handleToast("Insufficient balance", "error");
        actions.setSubmitting(false);
        return;
      }

      let valuesToBeUpdated = values;
      valuesToBeUpdated = {
        ...formValues,
      };
      valuesToBeUpdated.enteredAmount = values?.enteredAmount || 0;
      valuesToBeUpdated.quoteId = values?.quoteId || "";
      valuesToBeUpdated.receivedAmount = values?.receivedAmount || "";

      dispatch(setValues(valuesToBeUpdated));
      actions.setTouched({});

      const payload = {
        userId: valuesToBeUpdated?.contactId?.value,
        amount: valuesToBeUpdated?.receivedAmount,
        currency: valuesToBeUpdated?.payoutCurrency?.value,
        paymentPurpose: "transaction_between_banks", // hardcoded payment purpose
        paymentCode: valuesToBeUpdated?.paymentCode?.value,
        quoteId: valuesToBeUpdated?.quoteId,
        balanceCurrency: valuesToBeUpdated?.walletName?.value,
      };
      try {
        const response = await V2Service.createFiatPayoutOrder(payload);
        const {
          data: { data = {} },
        } = response || {};
        const parseResponse = JSON.parse(data);

        handleToast(`Order ${parseResponse?.orderId} created successfully`, "success");
        dispatch(goToNextStep());
        dispatch(setValues({}));
      } catch (error) {
        const errorObject = error?.response?.data?.error?.message || {};
        let errorMessage = errorObject?.message || "Something went wrong";
        const errorCode = errorObject?.code || "";

        console.log(errorCode, errorMessage);
        if (errorCode === "QUOTES_EXPIRED") {
          formikRef?.current?.setFieldValue("enteredAmount", 0);
          formikRef?.current?.setFieldValue("receivedAmount", 0);
          dispatch(setSendAmount(0));
          dispatch(setReceivedAmount(0));

          errorMessage = "Quote Expired - Please Retry for new Quotes";
        }
        handleToast(errorMessage, "error");
      } finally {
        actions.setSubmitting(false);
      }
    } else {
      dispatch(goToNextStep());
      let valuesToBeUpdated = values;

      if (activeStep === 2) {
        valuesToBeUpdated = {
          ...formValues,
        };
        valuesToBeUpdated.enteredAmount = values?.enteredAmount || 0;
        valuesToBeUpdated.quoteId = values?.quoteId || "";
        valuesToBeUpdated.receivedAmount = values?.receivedAmount || "";
      }
      dispatch(setValues(valuesToBeUpdated));
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }

  const fetchBalanceAccounts = async () => {
    const { payload = {} } = await dispatch(getBalanceAccounts());
    const {
      data: { response = [] },
    } = payload || {};
    setWalletList(response);
  };

  useEffect(() => {
    fetchBalanceAccounts();
    return () => {
      dispatch(resetState());
    };
  }, []);

  return loading ? (
    <IndividualPaymentLoader />
  ) : (
    <Box className="sm-main-card">
      <Typography type="largeheader" weight="bold">
        {"Settlements"}
      </Typography>
      <HStack display="flex" flexDirection="row" alignItems="flex-start">
        <Flex mt={"30px"} ml={"40px"} gap={6}>
          <Flex
            direction={"column"}
            gap={7}
            borderLeft={"2px solid"}
            borderLeftColor={"#1A4FD6"}
            py={"10px"}
            px={"10px"}
            h={"136px"}
            justify={"center"}
            w={"185px"}
          >
            <Button onClick={() => handleTabChange({ tabNumber: 1 })} _hover={{}} _active={{}} className="stepper-button">
              <Typography color="primary" colorweight={activeStep === 1 ? "500" : "200"} type="body" weight="bold">
                {"Select Contact"}
              </Typography>
            </Button>
            <Button onClick={() => handleTabChange({ tabNumber: 2 })} _hover={{}} _active={{}} className="stepper-button" isDisabled={!firstStepComplete}>
              <Typography color="primary" colorweight={activeStep === 2 ? "500" : "200"} type="body" weight="bold">
                {"Set Amount"}
              </Typography>
            </Button>
          </Flex>
        </Flex>
        <Formik initialValues={initialValue} onSubmit={handleSubmit} validateOnChange={false} validationSchema={currentValidationSchema} innerRef={formikRef}>
          {({ isSubmitting, handleSubmit }) => (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              {activeStep === 1 ? (
                <SelectContactTab isOpen={isOpen} onClose={onClose} walletList={walletList} />
              ) : activeStep === 2 ? (
                <SetAmountTab isSubmitting={isSubmitting} />
              ) : (
                <>{navigate("/order/pay-out-order")}</>
              )}
            </form>
          )}
        </Formik>
      </HStack>
    </Box>
  );
};

export default IndividualPayment;
